import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  ConfigField,
  ConfigGETResponseSuccess,
  JobSortableAttribute,
  mockedClient,
  mockedPermissions,
  mockedConfig,
  PermissionsListData,
  SortOrder,
} from '@contracts'
import useApi, { ApiResponse, FetchReq } from 'hooks/useApi'
import { FilterReducerState, IdValuePair } from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import { HomepageView } from 'pages/Home/HomePage.d'
import { extractFeatureFlags, getNewFilterSelection } from './utils'
import { FilterId } from 'utils/constants'
import { FEApiError } from 'utils/errorHandling'
import { OsContextValue } from '@wppopen/react'
import useOsContext from 'hooks/useOsContext'
import { getType } from 'utils'
import type { ClientsListDataV5 } from 'utils/utils.d'

/**
 * Represents a preserved filter state used for pre-selecting filters.
 * When a user interacts with a filter and then navigates/returns to the page, the filter state should be preserved.
 *
 * @property {string} filterId - The unique identifier (page level) of the filter object. e.g. 'clients' | 'brands'
 * @property {IdValuePair[] | string[]} activeFilters - The current filter values, based on the last interaction
 */
export interface FilterLoadData {
  filterId: string
  activeFilters: IdValuePair[] | string[]
}
export interface SortingContextState {
  sortBy: JobSortableAttribute | null
  sortOrder: SortOrder | null
}

export interface BannerViewStatus {
  shouldShow: boolean
  hasBeenViewed: boolean
  isDraft?: boolean
}

export type CurrentClients = ClientsListDataV5['clients'] | null

// Define the shape of the context data
export interface ContextData {
  // deprecated prop, extract from clientsApiMethods instead
  fetchClientData: FetchReq<ClientsListDataV5>
  getPermissionsApiClient: ApiResponse<PermissionsListData>
  // deprecated prop, extract from clientsApiMethods instead
  isClientApiLoading: boolean
  // deprecated prop, extract from clientsApiMethods instead
  clientApiError: FEApiError | null
  // deprecated prop, extract from clientsApiMethods instead
  clientsData: ClientsListDataV5 | null
  clientsApiMethods: ApiResponse<ClientsListDataV5>
  configApi: ApiResponse<ConfigGETResponseSuccess>
  totalNumberJobIds: number
  setTotalNumberJobIds: Dispatch<SetStateAction<number>>
  loadedJobIds: string[]
  setLoadedJobIds: Dispatch<SetStateAction<string[]>>
  preservedFilterSelection: Record<string, FilterLoadData[]>
  updatePreservedFilterSelection: (filtersId: FilterId, filterSelection: FilterLoadData[]) => void
  updatePreservedFilterSelectionWithReducer: (filterReducerState: FilterReducerState) => void
  preservedSortingSelection: SortingContextState
  setPreservedSortingSelection: Dispatch<SetStateAction<SortingContextState>>
  newProjectIDForBanner: string | null
  setNewProjectIdForBanner: Dispatch<SetStateAction<string | null>>
  currentClients: CurrentClients
  setCurrentClients: Dispatch<SetStateAction<CurrentClients>>
  currentView: HomepageView
  setCurrentView: Dispatch<SetStateAction<HomepageView>>
  featureFlags: { [K in ConfigField]?: boolean }
  history: string[]
  osContextValue?: OsContextValue | null
  hasUnsavedChanges: boolean
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>
  importedClients?: ClientsListDataV5['clients']
}

export function getMockedApiMethods<T>(inputMethods: Pick<ApiResponse<T>, 'fetchData'> & Partial<ApiResponse<T>>) {
  return {
    data: null,
    isLoading: false,
    error: null,
    url: new URL('https://example.com'),
    setUrl: () => {},
    ...inputMethods,
  } as ApiResponse<T>
}

// Create the context
export const AppContext = createContext<ContextData>({
  fetchClientData: async () => mockedClient as any,
  getPermissionsApiClient: getMockedApiMethods<PermissionsListData>({ fetchData: async () => mockedPermissions }),
  isClientApiLoading: true,
  clientApiError: null,
  clientsData: null,
  clientsApiMethods: getMockedApiMethods<ClientsListDataV5>({ fetchData: async () => mockedClient as any }),
  configApi: getMockedApiMethods<ConfigGETResponseSuccess>({ fetchData: async () => mockedConfig }),
  totalNumberJobIds: 0,
  setTotalNumberJobIds: () => {},
  loadedJobIds: [],
  setLoadedJobIds: () => {},
  preservedFilterSelection: {},
  updatePreservedFilterSelection: () => {},
  updatePreservedFilterSelectionWithReducer: () => {},
  preservedSortingSelection: { sortBy: null, sortOrder: null },
  setPreservedSortingSelection: () => {},
  newProjectIDForBanner: null,
  setNewProjectIdForBanner: () => {},
  currentClients: null,
  setCurrentClients: () => {},
  currentView: 'project',
  setCurrentView: () => {},
  featureFlags: {},
  history: [],
  osContextValue: undefined,
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => {},
})

interface AppProviderProps {
  osContextValue?: OsContextValue | null
}

export function AppProvider({ children, osContextValue }: PropsWithChildren<AppProviderProps>) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const location = useLocation()
  const [history, setHistory] = useState<string[]>([])

  const { account_ids, bearerHeader } = useOsContext({
    wppOpenOsContextOverride: osContextValue,
  })

  const authFetchOptions = useMemo(() => {
    return getType<RequestInit>({
      headers: !!osContextValue
        ? {
            ...bearerHeader,
            ...account_ids,
          }
        : {},
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearerHeader, account_ids])

  const clientsApiMethods = useApi<ClientsListDataV5>('clients', authFetchOptions)

  const {
    fetchData: fetchClientData,
    data: clientsData,
    isLoading: isClientApiLoading,
    error: clientApiError,
    url: fetchClientUrl,
  } = clientsApiMethods

  const getPermissionsApiClient = useApi<PermissionsListData>('permissions')

  const {
    fetchData: getPermissions,
    data: permissionsData,
    isLoading: isGetPermissionsLoading,
    url: getPermissionsUrl,
  } = getPermissionsApiClient

  const importedClients = useMemo(() => {
    return clientsData?.clients?.filter(client => client.created_at !== null) || []
  }, [clientsData])

  const configApi = useApi<ConfigGETResponseSuccess>('config', authFetchOptions)

  const { fetchData: fetchConfig, data: configData, isLoading: isConfigApiLoading, url: fetchConfigUrl } = configApi

  const [totalNumberJobIds, setTotalNumberJobIds] = useState<number>(0)
  const [loadedJobIds, setLoadedJobIds] = useState<string[]>([])
  const [preservedFilterSelection, setPreservedFilterSelection] = useState<Record<string, FilterLoadData[]>>({})
  const [preservedSortingSelection, setPreservedSortingSelection] = useState<SortingContextState>({
    sortBy: null,
    sortOrder: null,
  })
  const [newProjectIDForBanner, setNewProjectIdForBanner] = useState<string | null>(null)
  const [currentClients, setCurrentClients] = useState<CurrentClients>(null)
  const [currentView, setCurrentView] = useState<HomepageView>('project')
  const [featureFlags, setFeatureFlags] = useState<{ [K in ConfigField]?: boolean }>({})

  // update the state history when the location changes
  useEffect(() => {
    // if latest history item is === as location.pathname, then don't do anything
    if (history[0] === location.pathname) {
      return
    }

    setHistory(prevHistory => {
      const lastItem = prevHistory[0]
      const secondLastItem = prevHistory?.[1]
      const history = prevHistory
      // if new location is equal to the previous location, then remove that location
      if (lastItem === location.pathname) {
        return history
      }

      // if new location is equal to the previous [2] location, then remove that all up to that location
      if (secondLastItem === location.pathname) {
        return [location.pathname, ...history.slice(2)]
      }

      return [location.pathname, ...history]
    })
  }, [location])

  useEffect(() => {
    if (osContextValue === undefined) return
    if (!clientsData && !isClientApiLoading) {
      fetchClientData(fetchClientUrl, authFetchOptions)
    }
    if (!permissionsData && !isGetPermissionsLoading) {
      getPermissions(getPermissionsUrl, authFetchOptions)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (osContextValue === undefined) return
    if (!configData && !isConfigApiLoading) {
      fetchConfig(fetchConfigUrl)
        .then((res: ConfigGETResponseSuccess) => {
          const featureFlags = extractFeatureFlags(res)
          setFeatureFlags(featureFlags)
          return featureFlags
        })
        .then(featureFlags => {
          window.localStorage.setItem('featureFlags', JSON.stringify(featureFlags))
        })
        .catch((error: Error) => {
          console.error(error)
          // eslint-disable-next-line no-console
          console.warn('Error in Feature Flag call, falling back to local-storage feature flag state.')
          const featureFlags = JSON.parse(window.localStorage.getItem('featureFlags') || '')
          setFeatureFlags(featureFlags)
        })
    }
  }, [])

  const updatePreservedFilterSelection = (reducerId: FilterId, filterSelection: FilterLoadData[]) => {
    const newFilterSelection: Record<string, FilterLoadData[]> = getNewFilterSelection(
      reducerId,
      filterSelection,
      preservedFilterSelection,
    )
    setPreservedFilterSelection(newFilterSelection)
  }

  const updatePreservedFilterSelectionWithReducer = (filterReducerState: FilterReducerState) => {
    const { reducerId, index } = filterReducerState

    const filterSelection: FilterLoadData[] = Object.values(index).flatMap(({ id, activeFilters }) => {
      return activeFilters.length ? [{ filterId: id, activeFilters }] : []
    })

    updatePreservedFilterSelection(reducerId, filterSelection)
  }

  return (
    <AppContext.Provider
      value={{
        fetchClientData,
        getPermissionsApiClient,
        clientsData,
        isClientApiLoading,
        clientApiError,
        clientsApiMethods,
        configApi,
        totalNumberJobIds,
        setTotalNumberJobIds,
        loadedJobIds,
        setLoadedJobIds,
        preservedFilterSelection,
        updatePreservedFilterSelection,
        updatePreservedFilterSelectionWithReducer,
        preservedSortingSelection,
        setPreservedSortingSelection,
        newProjectIDForBanner,
        setNewProjectIdForBanner,
        currentClients,
        setCurrentClients,
        currentView,
        setCurrentView,
        featureFlags,
        history,
        osContextValue,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        importedClients,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export default AppContext
